import { MoreHorizontal } from "lucide-react";
import React from "react";

import { getDevice } from "@/utils/device";
import { useDeviceContext } from "../../../context/DeviceContext";
import { updateColourScheme } from "../../../utils/nativeUtils/customActions";
import { FeatureSentiment } from "../../Shared/FeatureSentiment";
import EDSThemeToggle from "../../Shared/ThemeToggle/EDSThemeToggle";
import { Popover, PopoverContent, PopoverTrigger } from "../../ui/popover";
import FullScreen from "./FullScreen";
import { useHeaderFlags } from "./useHeaderFlags";

type HeaderOverflowMenuProps = {
  featureSentimentName?: string;
};

export const HeaderOverflowMenu: React.FC<HeaderOverflowMenuProps> = ({
  featureSentimentName
}) => {
  const { colourScheme, setTheme } = useDeviceContext();
  const { forceIpad } = useHeaderFlags();
  // Fullscreen only makes sense on a desktop browser — hide it on iPad /
  // iPhone / embedded WebView where there's no browser chrome to toggle.
  const showFullscreen = getDevice() === "desktop" && !forceIpad;

  const handleThemeToggle = async (isDarkMode: boolean) => {
    const themeName = isDarkMode ? "Dark" : "Light";
    setTheme(themeName);
    await updateColourScheme(themeName);
  };

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          aria-label="More header options"
          className="eds-ghost-icon-button"
        >
          <MoreHorizontal />
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-64 p-3">
        <div className="flex flex-col gap-3">
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium">Theme</span>
            <EDSThemeToggle
              isDarkMode={colourScheme === "dark"}
              onToggle={handleThemeToggle}
            />
          </div>
          {showFullscreen && (
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium">Fullscreen</span>
              <FullScreen />
            </div>
          )}
          {featureSentimentName && (
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium">Feedback</span>
              <FeatureSentiment feature={featureSentimentName} />
            </div>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
};
